/**
 * Operations Insights App — admin-only visualization of coordinator workload
 * and study activity across Status Board, eReg, and Site Organizer.
 *
 * Guard = the standard module ProtectedRoute (super_admin bypass; /api/my-tools
 * 'ops_insights' entitlement) PLUS an administrator/super_admin role
 * requirement: this tool profiles coordinators, so it is admin-only by design
 * (also enforced server-side on every /api/ops-insights route).
 * Note: InsightsProvider lives in App.tsx's ModuleProviders so filter state
 * persists across cross-module navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { StudiesDashboard, CoordinatorsView } from './pages';
import { Layout } from './components';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { EmptyState } from '../ui';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, user: authUser, currentSite, sitesLoading } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  const isAdmin = authUser?.role === 'administrator' || authUser?.role === 'super_admin';

  // Entitlement is per SITE, so this effect re-runs on every site switch. It
  // must re-enter the checking state at the top (the IXVerifyApp pattern) —
  // otherwise the previous site's verdict keeps rendering while the new check
  // is still in flight, briefly showing a granted page (or an Access Denied)
  // for a site nothing has been checked for yet. `cancelled` keeps a slow
  // response from a since-abandoned site from landing on top of a newer one.
  useEffect(() => {
    let cancelled = false;

    const checkEntitlement = async () => {
      if (authUser?.role === 'super_admin') {
        if (cancelled) return;
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const insightsTool = tools.find((t: any) => t.tool_id === 'ops_insights');
        if (cancelled) return;
        setHasEntitlement(insightsTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Operations Insights entitlement:', error);
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        if (!cancelled) setCheckingEntitlement(false);
      }
    };

    if (session && !sitesLoading) {
      setCheckingEntitlement(true);
      checkEntitlement();
    }

    return () => {
      cancelled = true;
    };
  }, [session, authUser?.role, currentSite, sitesLoading]);

  // Check BEFORE the loading spinner: the entitlement effect only runs with
  // a session, so a session-less visit would leave checkingEntitlement true
  // forever and spin indefinitely instead of redirecting.
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (sitesLoading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // Admin-only, regardless of entitlement.
  const denied =
    !isAdmin || (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin');

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {!isAdmin
              ? 'Operations Insights is available to administrators only.'
              : 'You do not have access to Operations Insights for your current site. Please contact your administrator if you believe this is an error.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // super_admin bypasses the entitlement check above without requiring a
  // currentSite — reachable when /api/sites fails or returns empty. Without
  // a site, useInsightsData(null, ...) never resolves its loading state, so
  // the pages would spin forever; show a proper empty state instead.
  if (!currentSite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <EmptyState
          icon={Building2}
          title="No site selected"
          hint="Select a site from the header to use Operations Insights."
        />
      </div>
    );
  }

  return <>{children}</>;
}

// Fix Spec C: ProtectedRoute + Layout are hoisted ABOVE <Routes> so they
// mount ONCE for the module, not once per matched route. Before this fix,
// each Route element wrapped itself in its own <ProtectedRoute><Layout>,
// so switching between /insights/studies and /insights/coordinators
// remounted both — re-running the /api/my-tools entitlement check and the
// AppBar shell on every tab switch.
function InsightsRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/insights/studies" replace />} />
      <Route path="/studies" element={<StudiesDashboard />} />
      <Route path="/coordinators" element={<CoordinatorsView />} />
      <Route path="*" element={<Navigate to="/insights/studies" replace />} />
    </Routes>
  );
}

export function InsightsApp() {
  return (
    <ProtectedRoute>
      <Layout>
        <InsightsRoutes />
      </Layout>
    </ProtectedRoute>
  );
}

export default InsightsApp;
